/* eslint-disable jsx-a11y/no-static-element-interactions */
/**
 * ImageCropModal — self-contained 16:9 image cropper (zero external deps).
 *
 * Mirrors the mobile "Create Custom Category" crop step so what the user frames
 * is exactly what the category tile shows (tile renders 16:9 with object-fit:cover,
 * so the crop aspect MUST equal the display aspect — 16:9 — or content gets clipped).
 *
 * Behaviour (matches the mobile crop_your_image spec):
 *   - Centered, fixed 16:9 crop window; aspect is locked, user cannot change it.
 *   - The FULL image stays visible behind the window and moves/scales: drag to pan,
 *     wheel/pinch/slider to zoom. The area OUTSIDE the crop window is not hidden —
 *     it stays visible under a black 60% mask so the user sees what's excluded.
 *   - "Crop & Continue" exports the framed region as PNG bytes (only the crop is emitted).
 *
 * Props:
 *   open            {boolean}
 *   imageSrc        {string}   object URL / data URL of the picked image
 *   fileName        {string}   original file name (used to name the PNG output)
 *   aspect          {number}   crop aspect ratio (default 16/9)
 *   onCancel        {Function} () => void   — aborts, emits nothing
 *   onCropComplete  {Function} (pngFile: File) => void — receives the cropped PNG File
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  IconButton,
  CircularProgress,
  Slider,
} from '@mui/material';
import { Close, ZoomIn, ZoomOut } from '@mui/icons-material';

const ASPECT = 16 / 9;
const MAX_ZOOM = 4;
// Crop window width as a fraction of the stage, so the surrounding image stays
// visible (dimmed) around the window — matching the mobile crop view.
const CROP_FRAC = 0.82;
// Cap the exported PNG at 720p-width (16:9 → 1280×720). Plenty for a tile; keeps
// the upload small. ImageKit downscales further at display time.
const MAX_OUTPUT_WIDTH = 1280;

const ImageCropModal = ({
  open,
  imageSrc,
  fileName = 'banner',
  aspect = ASPECT,
  onCancel,
  onCropComplete,
}) => {
  const stageRef = useRef(null);
  const imgRef = useRef(null);
  // Natural dimensions of the source image.
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  // Zoom multiplier applied on top of the cover-fit base scale (>= 1).
  const [zoom, setZoom] = useState(1);
  // Pan offset (px, in the stage's coordinate space) from centered.
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Mirror latest state into a ref so gesture handlers always read fresh values.
  const stateRef = useRef({ zoom: 1, offset: { x: 0, y: 0 }, natural: { w: 0, h: 0 } });
  stateRef.current = { zoom, offset, natural };

  // Geometry derived from the current stage size + image + zoom.
  // cropW/cropH = the 16:9 window. At zoom 1 the image covers the WHOLE stage
  // (fills edge-to-edge, no grey gaps). minZoom lets the user zoom OUT until the
  // image shrinks to just cover the crop window (the framing view with margins).
  // eff = display px per natural px; dispW/dispH = rendered size.
  const computeGeom = useCallback((z = stateRef.current.zoom) => {
    const stage = stageRef.current;
    const nat = stateRef.current.natural;
    if (!stage || !nat.w || !nat.h) return null;
    const { width: sW, height: sH } = stage.getBoundingClientRect();
    const cropW = sW * CROP_FRAC;
    const cropH = cropW / aspect;
    const coverStage = Math.max(sW / nat.w, sH / nat.h); // fill the whole stage
    const coverWindow = Math.max(cropW / nat.w, cropH / nat.h); // just cover the window
    const minZoom = coverWindow / coverStage; // <= 1
    const eff = coverStage * z;
    return { sW, sH, cropW, cropH, minZoom, eff, dispW: nat.w * eff, dispH: nat.h * eff };
  }, [aspect]);

  // Clamp the pan so the crop window is always fully covered by the image.
  const clampOffset = useCallback((off, z = stateRef.current.zoom) => {
    const g = computeGeom(z);
    if (!g) return off;
    const maxX = Math.max(0, (g.dispW - g.cropW) / 2);
    const maxY = Math.max(0, (g.dispH - g.cropH) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, off.x)),
      y: Math.min(maxY, Math.max(-maxY, off.y)),
    };
  }, [computeGeom]);

  // Reset transform whenever a new image is loaded/opened.
  useEffect(() => {
    if (open) {
      setZoom(1);
      setOffset({ x: 0, y: 0 });
      setProcessing(false);
    }
  }, [open, imageSrc]);

  const handleImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    setNatural({ w: naturalWidth, h: naturalHeight });
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };

  const applyZoom = useCallback((nextZoom) => {
    const g = computeGeom();
    const min = g ? g.minZoom : 1;
    const z = Math.min(MAX_ZOOM, Math.max(min, nextZoom));
    setZoom(z);
    setOffset((prev) => clampOffset(prev, z));
  }, [computeGeom, clampOffset]);

  // ---- Pointer-based pan + pinch (works for mouse, trackpad and touch) ----
  // Active pointers keyed by pointerId; setPointerCapture keeps move events
  // flowing to the stage even if the cursor drifts outside it during a drag.
  const pointersRef = useRef(new Map());
  const dragStartRef = useRef(null); // { x, y, offX, offY }
  const pinchStartRef = useRef(null); // { dist, zoom }

  const handlePointerDown = (e) => {
    e.preventDefault();
    if (e.currentTarget.setPointerCapture) {
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
    }
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const count = pointersRef.current.size;
    if (count === 1) {
      setDragging(true);
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        offX: stateRef.current.offset.x,
        offY: stateRef.current.offset.y,
      };
    } else if (count === 2) {
      const [a, b] = [...pointersRef.current.values()];
      pinchStartRef.current = {
        dist: Math.hypot(a.x - b.x, a.y - b.y),
        zoom: stateRef.current.zoom,
      };
      dragStartRef.current = null;
      setDragging(false);
    }
  };

  const handlePointerMove = (e) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointersRef.current.values()];
    if (pts.length >= 2 && pinchStartRef.current) {
      const d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      applyZoom(pinchStartRef.current.zoom * (d / pinchStartRef.current.dist));
    } else if (pts.length === 1 && dragStartRef.current) {
      const s = dragStartRef.current;
      setOffset(clampOffset({
        x: s.offX + (e.clientX - s.x),
        y: s.offY + (e.clientY - s.y),
      }));
    }
  };

  const handlePointerUp = (e) => {
    if (e.currentTarget.releasePointerCapture) {
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
    }
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchStartRef.current = null;
    if (pointersRef.current.size === 0) {
      dragStartRef.current = null;
      setDragging(false);
    }
  };

  // Wheel: trackpad two-finger scroll → pan; pinch (ctrl/⌘ + wheel) → zoom.
  // Bound imperatively with { passive: false } so preventDefault works.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !open) return undefined;

    const handleWheel = (e) => {
      e.preventDefault();
      if (e.ctrlKey || e.metaKey) {
        const delta = -e.deltaY * 0.01;
        applyZoom(stateRef.current.zoom * (1 + delta));
      } else {
        setOffset(clampOffset({
          x: stateRef.current.offset.x - e.deltaX,
          y: stateRef.current.offset.y - e.deltaY,
        }));
      }
    };

    stage.addEventListener('wheel', handleWheel, { passive: false });
    return () => stage.removeEventListener('wheel', handleWheel);
  }, [open, applyZoom, clampOffset]);

  // ---- Produce the cropped PNG ----
  const handleConfirm = async () => {
    const img = imgRef.current;
    const g = computeGeom();
    if (!img || !g) return;

    setProcessing(true);
    try {
      const { cropW, cropH, eff, dispW, dispH } = g;

      // The crop window (centered) mapped back to natural image coordinates.
      let natX = (dispW / 2 - cropW / 2 - offset.x) / eff;
      let natY = (dispH / 2 - cropH / 2 - offset.y) / eff;
      let natW = cropW / eff;
      let natH = cropH / eff;

      // Guard against rounding drift outside the image bounds.
      natX = Math.max(0, Math.min(natX, natural.w));
      natY = Math.max(0, Math.min(natY, natural.h));
      natW = Math.min(natW, natural.w - natX);
      natH = Math.min(natH, natural.h - natY);

      const outW = Math.min(MAX_OUTPUT_WIDTH, Math.round(natW));
      const outH = Math.round(outW / aspect);

      const canvas = document.createElement('canvas');
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#000000'; // black background per spec (letterbox guard)
      ctx.fillRect(0, 0, outW, outH);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, natX, natY, natW, natH, 0, 0, outW, outH);

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('Crop failed');

      const base = (fileName || 'banner').replace(/\.[^.]+$/, '');
      const pngFile = new File([blob], `${base}.png`, { type: 'image/png' });
      onCropComplete(pngFile);
    } catch (err) {
      setProcessing(false);
    }
  };

  const geom = computeGeom();

  return (
    <Dialog
      open={open}
      onClose={processing ? undefined : onCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{ style: { borderRadius: 16, maxWidth: 860, width: '90vw' } }}
    >
      <DialogContent style={{ padding: 0 }}>
        {/* Header */}
        <div style={{
          padding: '16px 24px',
          backgroundColor: '#F8F9FA',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
        >
          <span style={{
            color: '#1A1A1A',
            fontSize: 18,
            fontWeight: 600,
            fontFamily: 'Montserrat, sans-serif',
          }}
          >
            Crop Image
          </span>
          <IconButton
            onClick={processing ? undefined : onCancel}
            size="small"
            style={{ color: '#6B7280' }}
          >
            <Close />
          </IconButton>
        </div>

        {/* Crop stage */}
        <div style={{ padding: 24 }}>
          <div
            ref={stageRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            style={{
              position: 'relative',
              width: '100%',
              aspectRatio: `${aspect}`,
              backgroundColor: 'transparent',
              overflow: 'hidden',
              borderRadius: 10,
              cursor: dragging ? 'grabbing' : 'grab',
              touchAction: 'none',
              userSelect: 'none',
            }}
          >
            {imageSrc && (
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Crop preview"
                onLoad={handleImageLoad}
                draggable={false}
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: geom ? `${geom.dispW}px` : 'auto',
                  height: geom ? `${geom.dispH}px` : 'auto',
                  transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
                  maxWidth: 'none',
                  pointerEvents: 'none',
                }}
              />
            )}

            {/* 16:9 crop window — the box-shadow paints the 60% black mask over
                the surrounding (still-visible) image; the border is the guide. */}
            <div style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: `${CROP_FRAC * 100}%`,
              aspectRatio: `${aspect}`,
              transform: 'translate(-50%, -50%)',
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
              border: '1.5px solid rgba(255, 255, 255, 0.9)',
              borderRadius: 4,
              pointerEvents: 'none',
            }}
            />
          </div>

          {/* Zoom control */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '16px 4px 0',
          }}
          >
            <ZoomOut style={{ color: '#6B7280', fontSize: 20 }} />
            <Slider
              value={zoom}
              min={geom ? geom.minZoom : 1}
              max={MAX_ZOOM}
              step={0.01}
              onChange={(_, v) => applyZoom(v)}
              style={{ color: '#5aa5ef', flex: 1 }}
              aria-label="Zoom"
            />
            <ZoomIn style={{ color: '#6B7280', fontSize: 20 }} />
          </div>
          <p style={{
            color: '#9CA3AF',
            fontSize: 12,
            textAlign: 'center',
            margin: '4px 0 0',
            fontFamily: 'Montserrat, sans-serif',
          }}
          >
            Drag to reposition · Scroll or pinch to zoom
          </p>
        </div>

        {/* Footer */}
        <div style={{
          padding: 24,
          backgroundColor: '#F8F9FA',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 12,
        }}
        >
          <Button
            onClick={onCancel}
            disabled={processing}
            style={{
              padding: '10px 22px',
              color: '#6B7280',
              fontSize: 14,
              fontFamily: 'Montserrat, sans-serif',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={processing || !natural.w}
            variant="contained"
            style={{
              backgroundColor: processing || !natural.w ? '#E5E7EB' : '#5aa5ef',
              color: 'white',
              borderRadius: 8,
              padding: '10px 22px',
              fontSize: 14,
              fontWeight: 500,
              fontFamily: 'Montserrat, sans-serif',
              boxShadow: 'none',
              minWidth: 150,
            }}
          >
            {processing ? (
              <CircularProgress size={20} style={{ color: 'white' }} />
            ) : (
              'Crop & Continue'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropModal;
